import * as React from 'react';

import {exhaustivenessCheck} from 'nutshell-core/typing/exhaustiveness-check';
import {getIdFromApiId} from 'nutshell-core/utils/get-id-from-api-id';

import type {AddButtonPropsMultiple, EmptyStateProps} from 'shells/card/card-widget/card-widget';
import type {TabProps} from 'shells/control-bar/page-control-bar';
import {BoxRegularIcon} from 'shells/icon/svg/box-regular-icon';
import {InvoiceRegularDiagonalIcon} from 'shells/icon/svg/invoice-regular-diagonal-icon';
import {QuoteRegularIcon} from 'shells/icon/svg/quote-regular-icon';
import {
    getBadgeLevel,
    getIcon,
    getSalesDocumentStatusText,
    getSalesDocumentStatusTime,
} from 'shells/lead-sidebar/sales-documents/helpers';
import type {SalesDocumentType} from 'shells/lead-sidebar/sales-documents/types';
import type {BaseRowStatusBadge} from 'shells/row/base-row/base-row';

import type {QuoteDisplayStatus, SalesDocumentVariant} from 'nutshell-graphql-types';
import type {
    GetQuotes_quotes as Quote,
    GetInvoices_invoices as Invoice,
} from 'nutshell-graphql-types';

import {getQuoteTimes} from '../../../sales-documents/shared';

import {appHistory} from '../../../../../history';

import type {RevenueTab} from './revenue-widget';

export function getTabOptions(
    selectedTab: RevenueTab,
    handleTabChange: (tab: RevenueTab) => void,
    productCount: number,
    quotesCount: number,
    invoicesCount: number,
    hasOverdueInvoice: boolean,
    canUseSalesDocuments: boolean
): TabProps {
    // These ids match the RevenueTab type in revenue-widget.jsx
    const tabs = [
        {
            id: 'products',
            label: 'Products',
            count: String(productCount),
        },
    ];
    if (canUseSalesDocuments) {
        tabs.push({
            id: 'quotes',
            label: 'Quotes',
            count: String(quotesCount),
        });
        tabs.push({
            id: 'invoices',
            label: 'Invoices',
            count: String(invoicesCount),
            // @ts-expect-error
            badgeColor: hasOverdueInvoice ? 'rose' : undefined,
        });
    }

    return {
        options: tabs,
        selectedTab,
        onChangeTab: (tab: string) => {
            // @ts-expect-error
            handleTabChange(tab);
        },
    };
}

export function getAddButtonProps(
    handleTabChange: (tab: RevenueTab) => void,
    handleCreateSalesDocument: (salesDocumentVariant: SalesDocumentVariant) => void,
    setIsAddingProduct: (isAddingProduct: boolean) => void,
    canUseSalesDocuments: boolean
): AddButtonPropsMultiple {
    const selectMenuOptions = [
        {
            icon: <BoxRegularIcon size={16} wrapWithDiv={false} />,
            label: 'Add a product',
            value: 'product',
            handleSelect: () => {
                handleTabChange('products');
                setIsAddingProduct(true);
            },
        },
    ];
    if (canUseSalesDocuments) {
        selectMenuOptions.push({
            icon: <QuoteRegularIcon size={16} wrapWithDiv={false} />,
            label: 'Add a quote',
            value: 'quote',
            handleSelect: () => {
                handleTabChange('quotes');
                handleCreateSalesDocument('QUOTE');
            },
        });
        selectMenuOptions.push({
            icon: <InvoiceRegularDiagonalIcon size={16} wrapWithDiv={false} />,
            label: 'Add an invoice',
            value: 'invoice',
            handleSelect: () => {
                handleTabChange('invoices');
                handleCreateSalesDocument('INVOICE');
            },
        });
    }

    return {
        variant: 'multiple',
        selectMenuOptions: selectMenuOptions,
    };
}

export const handleOpenSalesDocument = (type: SalesDocumentType, id: string) => {
    const numericId = getIdFromApiId(id);
    let hashBase;
    if (type === 'Quote') {
        hashBase = 'quotes';
    } else {
        hashBase = 'invoices';
    }
    appHistory.replace({
        ...appHistory.location,
        hash: `/${hashBase}/${numericId}`,
        state: {modal: true},
    });
};

export function getSalesDocumentStatusBadgeProps(
    salesDocument: Quote | Invoice,
    type: SalesDocumentType,
    displayStatus: QuoteDisplayStatus,
    invoiceOverdueDuration?: string | null
): BaseRowStatusBadge {
    const salesDocumentTimes = getQuoteTimes(salesDocument);
    const icon = getIcon(displayStatus, type, invoiceOverdueDuration);

    return {
        text: getSalesDocumentStatusText(displayStatus, type, invoiceOverdueDuration),
        level: getBadgeLevel(displayStatus, invoiceOverdueDuration),
        detail: salesDocumentTimes
            ? getSalesDocumentStatusTime(
                  displayStatus,
                  type,
                  salesDocumentTimes,
                  invoiceOverdueDuration
              )
            : '',
        icon,
    };
}

export const handleSalesDocumentChange = (quoteModalUrl: string, refetch: () => Promise<any>) => {
    refetch().then(() => {
        window.open(quoteModalUrl, '_self');
    });
};

export const getIsLoading = (
    currentTab: RevenueTab,
    isProductsLoading: boolean,
    isQuotesLoading: boolean,
    isInvoicesLoading: boolean
): boolean => {
    switch (currentTab) {
        case 'products':
            return isProductsLoading;
        case 'quotes':
            return isQuotesLoading;
        case 'invoices':
            return isInvoicesLoading;
        default:
            exhaustivenessCheck(currentTab);
    }
};

export const getIsEmpty = (
    currentTab: RevenueTab,
    productCount: number,
    quotesCount: number,
    invoicesCount: number,
    isAddingProduct: boolean
): boolean => {
    switch (currentTab) {
        case 'products':
            return productCount === 0 && !isAddingProduct;
        case 'quotes':
            return quotesCount === 0;
        case 'invoices':
            return invoicesCount === 0;
        default:
            exhaustivenessCheck(currentTab);
    }
};

export const getEmptyStateProps = (selectedTab: RevenueTab, isEmpty: boolean): EmptyStateProps => {
    switch (selectedTab) {
        case 'quotes':
            return {
                isEmpty,
                text: 'No quotes added',
                icon: QuoteRegularIcon,
                iconSize: 22,
            };
        case 'invoices':
            return {
                isEmpty,
                text: 'No invoices added',
                icon: InvoiceRegularDiagonalIcon,
                iconSize: 22,
            };
        case 'products':
            return {
                isEmpty,
                text: 'No products added',
                icon: BoxRegularIcon,
                iconSize: 22,
            };
        default:
            exhaustivenessCheck(selectedTab);
    }
};
